import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { HMSPeer } from '@100mslive/hms-video-store';
import './d-video-conference-layout.js';
import {
  type DeviceMap,
  DeviceType,
  type HMSMediaSettings,
  HMSReactiveStore,
  selectDevices,
  selectIsConnectedToRoom,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectLocalMediaSettings,
  selectPeers,
  selectVideoTrackByID,
} from '@100mslive/hms-video-store';
import { HMSNotificationTypes } from '@100mslive/hms-video-store';
import type { Ref } from 'lit/directives/ref.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { ViewerPropertiesConfig } from '../services/viewer-properties-types';
import { viewerPropertiesManager } from '../services/viewer-properties-manager';
import type { Text as YText } from 'yjs';
import { t } from 'src/i18n';

import '@shoelace-style/shoelace/dist/themes/light.css';
import '@shoelace-style/shoelace/dist/components/button-group/button-group.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/dropdown/dropdown.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/visually-hidden/visually-hidden.js';
import '@shoelace-style/shoelace/dist/components/menu/menu.js';
import '@shoelace-style/shoelace/dist/components/menu-item/menu-item.js';

import { setBasePath } from '@shoelace-style/shoelace/dist/utilities/base-path.js';

setBasePath('/shoelace');

@customElement('d-meet-viewer')
export class DMeetViewer extends LitElement {
  static readonly styles = css`
    :host {
      display: grid;
      grid-template-rows: 1fr auto;
      grid-template-columns: 100%;
      width: 100%;
      height: 100%;
      overflow: hidden; /* Prevent scrolling within component */

      font-family:
        -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue',
        sans-serif;
      background-color: #263238;
      color: white;
    }

    h1,
    h2,
    h3,
    h4,
    h5 {
      font-weight: normal;
    }

    #header {
      padding: 10px;
      display: flex;
      align-items: end;
      justify-content: space-between;
    }

    .btn-danger {
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 6px 14px;
      background-color: #f44336;
      color: white;
      font-family: inherit;
      font-size: 14px;
    }

    .hide {
      display: none !important;
    }

    form {
      max-width: 450px;
      margin: 30px auto;
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
      border-radius: 8px;
      padding: 20px;
      width: 100%;
    }

    input {
      display: block;
      width: 100%;
      border-radius: 8px;
      border: 2px solid transparent;
      height: 34px;
      padding: 5px;
      background: #37474f;
      color: inherit;
      font-family: inherit;
    }

    input::placeholder {
      color: #aaa;
    }

    .input-container {
      margin-bottom: 20px;
    }

    .btn-primary {
      border: 1px solid transparent;
      border-radius: 4px;
      padding: 6px 14px;
      background-color: #1565c0;
      color: white;
      font-family: inherit;
      font-size: 14px;
    }

    form h2,
    .conference-section h2 {
      margin-bottom: 20px;
    }

    .conference-section {
      position: relative;
      width: 100%;
      height: 100%;
      padding: 0;
      margin: 0;
      overflow: hidden;
    }

    .conference-section h2 {
      text-align: center;
      font-size: 32px;
      padding-bottom: 10px;
      border-bottom: 1px solid #546e7a;
    }

    /* Top bar for device controls */
    .top-bar {
      position: absolute;
      top: 20px;
      top: max(20px, env(safe-area-inset-top)); /* Respect safe area on mobile */
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      gap: 12px;
      z-index: 100;
      opacity: 1;
      transition: opacity 0.3s ease;
    }

    .top-bar.hidden {
      opacity: 0;
      pointer-events: none;
    }

    .top-bar:hover {
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    /* Floating controls container */
    .floating-controls {
      position: absolute;
      left: 20px;
      top: 50%;
      transform: translateY(-50%);
      display: flex;
      flex-direction: column;
      gap: 16px;
      z-index: 100;
      opacity: 1;
      transition: opacity 0.3s ease;
    }

    .floating-controls.hidden {
      opacity: 0;
      pointer-events: none;
    }

    .floating-controls:hover {
      opacity: 1 !important;
      pointer-events: auto !important;
    }

    /* Floating button styles */
    .floating-btn {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      background: rgba(38, 50, 56, 0.8);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.1);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s ease;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      position: relative;
    }

    .floating-btn:hover {
      background: rgba(38, 50, 56, 0.95);
      transform: scale(1.1);
      box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
    }

    /* Button label styles */
    .floating-btn-wrapper {
      position: relative;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .floating-btn-label {
      position: absolute;
      left: calc(100% + 12px);
      white-space: nowrap;
      color: white;
      font-size: 14px;
      background: rgba(38, 50, 56, 0.9);
      backdrop-filter: blur(10px);
      padding: 6px 12px;
      border-radius: 6px;
      pointer-events: none;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    }

    .floating-btn.danger {
      background: rgba(244, 67, 54, 0.8);
    }

    .floating-btn.danger:hover {
      background: rgba(244, 67, 54, 0.95);
    }

    .floating-btn.active {
      background: rgba(33, 150, 243, 0.8);
    }

    /* Device selection dropdown */
    .device-dropdown {
      position: relative;
    }

    .device-menu {
      position: absolute;
      top: calc(100% + 8px);
      left: 50%;
      transform: translateX(-50%);
      background: rgba(38, 50, 56, 0.95);
      backdrop-filter: blur(10px);
      border-radius: 8px;
      padding: 8px;
      min-width: 200px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      display: none;
      z-index: 101;
    }

    .device-menu.show {
      display: block;
    }

    .device-menu-item {
      display: flex;
      align-items: center;
      padding: 8px 12px;
      color: white;
      cursor: pointer;
      border-radius: 4px;
      font-size: 14px;
      transition: background 0.2s;
    }

    .device-menu-item:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .device-menu-item.selected {
      background: rgba(33, 150, 243, 0.3);
    }

    .device-menu-item.selected::before {
      content: '✓';
      margin-right: 8px;
    }

    /* Video elements styles */
    .local {
      transform: scaleX(-1);
    }

    /* Ensure video elements fill their containers */
    video {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `;
  @property({ type: String })
  userName!: string;
  @state()
  deviceMap?: DeviceMap;
  @state()
  localMediaSettings?: HMSMediaSettings;
  @property({ type: String })
  roomCode = '';
  @property({ type: String })
  meetingAgenda = '';
  @property({ type: Boolean })
  privateNotesEnabled = false;
  @property({ type: String })
  organizationId = '';
  @property({ type: String })
  meetingOccurrenceId = '';
  @property({ type: String })
  currentEmployeeUuid = '';
  /**
   * Whether the viewer is in fullscreen mode (mobile) or sidebar mode (desktop).
   * In fullscreen mode, agenda and private notes are shown inline.
   * In sidebar mode, they are hidden (shown in main content area instead).
   */
  @property({ type: Boolean })
  isFullscreen = true;
  @state()
  private showAgenda = false;
  @state()
  private showPrivateNotes = false;
  @state()
  private sidebarContent: 'none' | 'agenda' | 'notes' = 'none';
  @state()
  private privateNotesContent = '';
  @state()
  private privateNotesLoading = false;
  @state()
  private privateNotesInitialized = false;
  private privateNotesText?: YText;
  private viewerPropertiesCleanup?: () => void;
  private currentViewerPropertiesKey?: string;
  private initializationPromise?: Promise<void>;
  @state()
  private showControls = true;
  @state()
  private showAudioDevices = false;
  @state()
  private showVideoDevices = false;
  @state()
  private showAudioOutputDevices = false;
  private hideControlsTimer?: number;
  // Initialize HMS Store
  private hmsManager = new HMSReactiveStore();
  private hmsStore = this.hmsManager.getStore();
  private hmsActions = this.hmsManager.getActions();
  private hmsNotifications = this.hmsManager.getNotifications();
  @state()
  private connected = false;
  @state()
  private peers: HMSPeer[] = [];
  private videoRefs: Map<string, Ref<Element>> = new Map();
  private selfVideoRef: Ref<HTMLVideoElement> = createRef();
  @state()
  private audioEnabled = true;
  @state()
  private videoEnabled = true;
  private selfVideoSubscribed = false;
  // Store bound functions for proper event listener cleanup
  private boundHandleScreenClick = this.handleScreenClick.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.hmsManager.triggerOnSubscribe();
    this.hmsStore.subscribe((b?: boolean) => this.onConnection(b), selectIsConnectedToRoom);
    this.hmsStore.subscribe(() => this.renderPeers(), selectPeers);
    this.hmsStore.subscribe(() => this.onUpdateDeviceMap(), selectDevices);
    this.hmsStore.subscribe((settings) => this.onUpdateLocalMediaSettings(settings), selectLocalMediaSettings);

    // Add click listener for showing controls
    this.addEventListener('click', this.boundHandleScreenClick);

    // Initialize private notes if feature is enabled and we have required params
    if (this.privateNotesEnabled) {
      this.initializePrivateNotes();
    }

    this.hmsNotifications.onNotification((notification) => {
      console.log('notification type', notification.type);
      console.log('data', notification.data);

      // you can use the following to show appropriate toast notifications for example
      switch (notification.type) {
        case HMSNotificationTypes.PEER_LIST:
          console.log(`${notification.data} are the peers in the room`); // received right after join
          break;
        case HMSNotificationTypes.PEER_JOINED:
          console.log(`${notification.data.name} joined`);
          break;
        case HMSNotificationTypes.PEER_LEFT:
          console.log(`${notification.data.name} left`);
          break;
        case HMSNotificationTypes.NEW_MESSAGE:
          console.log(`${notification.data.message} received from ${notification.data.senderName}`);
          break;
        case HMSNotificationTypes.ERROR:
          console.log('[Error]', notification.data);
          console.log('[Error Code]', notification.data.code);
          break;
        case HMSNotificationTypes.RECONNECTING:
          console.log('[Reconnecting]', notification.data);
          break;
        case HMSNotificationTypes.RECONNECTED:
          console.log('[Reconnected]');
          break;
        case HMSNotificationTypes.NAME_UPDATED:
        case HMSNotificationTypes.METADATA_UPDATED:
        case HMSNotificationTypes.ROLE_UPDATED:
          console.log(`peer updated(${notification.type}), new peer=`, notification.data);
          break;
        case HMSNotificationTypes.TRACK_DEGRADED:
          console.log(`track - ${notification.data} degraded due to poor network`);
          break;
        case HMSNotificationTypes.TRACK_RESTORED:
          console.log(`track - ${notification.data} recovered`);
          break;
        case HMSNotificationTypes.ROOM_ENDED:
          console.log(`room ended, reason - ${notification.data.reason}`);
          break;
        case HMSNotificationTypes.REMOVED_FROM_ROOM:
          console.log(`removed from room, reason - ${notification.data.reason}`);
          break;
        case HMSNotificationTypes.DEVICE_CHANGE_UPDATE:
          console.log(`device changed - ${notification.data}`);
          break;
        default:
          break;
      }
    });
    this.onJoinRoom().then(() => console.log('automatically connected'));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.boundHandleScreenClick);
    // Clean up YJS provider
    this.cleanupViewerProperties();
  }

  async onJoinRoom() {
    const authToken = await this.hmsActions.getAuthTokenByRoomCode({ roomCode: this.roomCode });

    await this.hmsActions.join({
      userName: this.userName,
      authToken,
    });
    this.connected = true;
  }

  async onLeaveRoom() {
    await this.hmsActions.leave();
    this.dispatchEvent(new CustomEvent('hide-meet', { bubbles: true, composed: true }));
  }

  async onMuteAudio() {
    this.audioEnabled = !this.hmsStore.getState(selectIsLocalAudioEnabled);
    await this.hmsActions.setLocalAudioEnabled(this.audioEnabled);
  }

  async onMuteVideo() {
    this.videoEnabled = !this.hmsStore.getState(selectIsLocalVideoEnabled);
    await this.hmsActions.setLocalVideoEnabled(this.videoEnabled);

    // Re-attach video to PIP when enabling video
    if (this.videoEnabled) {
      const localPeer = this.peers.find((p) => p.isLocal);
      if (localPeer && localPeer.videoTrack) {
        // Wait for next render cycle to ensure element is visible
        await this.updateComplete;
        setTimeout(() => this.attachSelfVideo(localPeer), 100);
      }
    }
  }

  onUpdateDeviceMap() {
    console.log('update device map');
    this.deviceMap = this.hmsStore.getState(selectDevices);
  }

  onUpdateLocalMediaSettings(settings: HMSMediaSettings) {
    console.log('update LocalMediaSettings');
    this.localMediaSettings = settings;
  }

  onConnection(isConnected?: boolean) {
    this.connected = isConnected === true;
    if (this.connected) {
      this.startHideControlsTimer();
    }
  }

  renderPeers() {
    console.log('renderPeers');
    this.peers = this.hmsStore.getState(selectPeers);
    // Setup self video subscription
    const localPeer = this.peers.find((p) => p.isLocal);
    if (localPeer && !this.selfVideoSubscribed) {
      this.setupSelfVideoSubscription(localPeer);
      this.selfVideoSubscribed = true;
    }
    // Try to attach video after next render
    if (localPeer && localPeer.videoTrack) {
      setTimeout(() => this.attachSelfVideo(localPeer), 100);
    }
  }

  protected render() {
    const selected = this.localMediaSettings;
    const deviceMap1 = this.deviceMap;
    console.log(deviceMap1);
    console.log(selected);

    // Get all peers
    const remotePeers = this.peers.filter((peer) => !peer.isLocal && peer.videoTrack !== undefined);
    const localPeer = this.peers.find((peer) => peer.isLocal);

    // Total participant count (local + remote)
    const participantCount = localPeer ? remotePeers.length + 1 : remotePeers.length;

    // Check if sidebar should be shown (only in fullscreen mode)
    const showSidebar = this.isFullscreen && this.sidebarContent !== 'none';

    return html`
      <form id="join" class="${this.connected ? 'hide' : ''}">
        <h2>${t('viewers.meet.joiningNow')}</h2>
        <p>${t('viewers.meet.connecting')}</p>
      </form>

      <div id="conference" class="conference-section ${this.connected ? '' : 'hide'}">
        <!-- Top bar for device selection (show only if multiple devices available) -->
        ${deviceMap1 &&
        (deviceMap1[DeviceType.audioInput]?.length > 1 ||
          deviceMap1[DeviceType.videoInput]?.length > 1 ||
          deviceMap1[DeviceType.audioOutput]?.length > 1)
          ? html`
              <div class="top-bar ${this.showControls ? '' : 'hidden'}">
                ${deviceMap1[DeviceType.audioInput]?.length > 1
                  ? html`
                      <div class="device-dropdown">
                        <button
                          class="floating-btn"
                          @click=${(e: MouseEvent) => {
                            e.stopPropagation();
                            this.showAudioDevices = !this.showAudioDevices;
                            this.showVideoDevices = false;
                            this.showAudioOutputDevices = false;
                          }}
                          title=${t('viewers.meet.selectMicrophone')}
                        >
                          <sl-icon name="mic-fill"></sl-icon>
                        </button>
                        <div class="device-menu ${this.showAudioDevices ? 'show' : ''}">
                          ${deviceMap1[DeviceType.audioInput].map(
                            (device) => html`
                              <div
                                class="device-menu-item ${device.deviceId === selected?.audioInputDeviceId
                                  ? 'selected'
                                  : ''}"
                                @click=${async (e: MouseEvent) => {
                                  e.stopPropagation();
                                  await this.onUpdateAudioDevice(device.deviceId);
                                  this.showAudioDevices = false;
                                }}
                              >
                                ${device.label}
                              </div>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}
                ${deviceMap1[DeviceType.videoInput]?.length > 1
                  ? html`
                      <div class="device-dropdown">
                        <button
                          class="floating-btn"
                          @click=${(e: MouseEvent) => {
                            e.stopPropagation();
                            this.showVideoDevices = !this.showVideoDevices;
                            this.showAudioDevices = false;
                            this.showAudioOutputDevices = false;
                          }}
                          title=${t('viewers.meet.selectCamera')}
                        >
                          <sl-icon name="camera-fill"></sl-icon>
                        </button>
                        <div class="device-menu ${this.showVideoDevices ? 'show' : ''}">
                          ${deviceMap1[DeviceType.videoInput].map(
                            (device) => html`
                              <div
                                class="device-menu-item ${device.deviceId === selected?.videoInputDeviceId
                                  ? 'selected'
                                  : ''}"
                                @click=${async (e: MouseEvent) => {
                                  e.stopPropagation();
                                  await this.onUpdateVideoDevice(device.deviceId);
                                  this.showVideoDevices = false;
                                }}
                              >
                                ${device.label}
                              </div>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}
                ${deviceMap1[DeviceType.audioOutput]?.length > 1
                  ? html`
                      <div class="device-dropdown">
                        <button
                          class="floating-btn"
                          @click=${(e: MouseEvent) => {
                            e.stopPropagation();
                            this.showAudioOutputDevices = !this.showAudioOutputDevices;
                            this.showAudioDevices = false;
                            this.showVideoDevices = false;
                          }}
                          title=${t('viewers.meet.selectSpeaker')}
                        >
                          <sl-icon name="speaker-fill"></sl-icon>
                        </button>
                        <div class="device-menu ${this.showAudioOutputDevices ? 'show' : ''}">
                          ${deviceMap1[DeviceType.audioOutput].map(
                            (device) => html`
                              <div
                                class="device-menu-item ${device.deviceId === selected?.audioOutputDeviceId
                                  ? 'selected'
                                  : ''}"
                                @click=${async (e: MouseEvent) => {
                                  e.stopPropagation();
                                  await this.onUpdateAudioOutputDevice(device.deviceId);
                                  this.showAudioOutputDevices = false;
                                }}
                              >
                                ${device.label}
                              </div>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}
              </div>
            `
          : nothing}

        <!-- Floating controls on left side (simplified) -->
        <div class="floating-controls ${this.showControls ? '' : 'hidden'}">
          <div class="floating-btn-wrapper">
            <button
              class="floating-btn ${this.audioEnabled ? '' : 'active'}"
              @click=${this.onMuteAudio}
              title="${this.audioEnabled ? t('viewers.meet.muteMicrophone') : t('viewers.meet.unmuteMicrophone')}"
            >
              <sl-icon name="${this.audioEnabled ? 'mic' : 'mic-mute'}"></sl-icon>
            </button>
            <span class="floating-btn-label">${t('viewers.meet.toggleAudio')}</span>
          </div>

          <div class="floating-btn-wrapper">
            <button
              class="floating-btn ${this.videoEnabled ? '' : 'active'}"
              @click=${this.onMuteVideo}
              title="${this.videoEnabled ? t('viewers.meet.muteCamera') : t('viewers.meet.unmuteCamera')}"
            >
              <sl-icon name="${this.videoEnabled ? 'camera-video' : 'camera-video-off'}"></sl-icon>
            </button>
            <span class="floating-btn-label">${t('viewers.meet.toggleVideo')}</span>
          </div>
          ${this.meetingAgenda && this.isFullscreen
            ? html`
                <div class="floating-btn-wrapper">
                  <button
                    class="floating-btn ${this.sidebarContent === 'agenda' ? 'active' : ''}"
                    @click=${() => (this.sidebarContent = this.sidebarContent === 'agenda' ? 'none' : 'agenda')}
                    title=${t('viewers.meet.toggleAgenda')}
                  >
                    <sl-icon name="list-task"></sl-icon>
                  </button>
                  <span class="floating-btn-label">${t('viewers.meet.agenda')}</span>
                </div>
              `
            : nothing}
          ${this.privateNotesEnabled && this.isFullscreen
            ? html`
                <div class="floating-btn-wrapper">
                  <button
                    class="floating-btn ${this.sidebarContent === 'notes' ? 'active' : ''}"
                    @click=${() => (this.sidebarContent = this.sidebarContent === 'notes' ? 'none' : 'notes')}
                    title=${t('viewers.meet.privateNotes')}
                  >
                    <sl-icon name="pencil-square"></sl-icon>
                  </button>
                  <span class="floating-btn-label">${t('viewers.meet.notes')}</span>
                </div>
              `
            : nothing}
          <div class="floating-btn-wrapper">
            <button class="floating-btn danger" @click=${this.onLeaveRoom} title=${t('viewers.meet.leaveMeeting')}>
              <sl-icon name="telephone-x"></sl-icon>
            </button>
            <span class="floating-btn-label">${t('viewers.meet.leaveMeeting')}</span>
          </div>
        </div>

        <!-- New video conference layout -->
        <d-video-conference-layout
          .participantCount=${participantCount}
          .showSidebar=${showSidebar}
          .layoutMode=${showSidebar ? 'with-sidebar' : 'fullscreen'}
        >
          <!-- Local peer (participant-0) -->
          ${localPeer
            ? html`
                <video
                  slot="participant-0"
                  class="local"
                  ${ref(this.selfVideoRef)}
                  autoplay
                  muted
                  playsinline
                  @loadedmetadata=${() => console.log('Self video metadata loaded')}
                  style="${this.videoEnabled ? '' : 'display: none;'}"
                ></video>
              `
            : nothing}

          <!-- Remote peers (participant-1, participant-2, etc.) -->
          ${remotePeers.map(
            (peer, index) => html`
              <video
                slot="participant-${index + 1}"
                ${ref((v?: Element) => this.createRef(peer, v))}
                autoplay
                muted
                playsinline
              ></video>
            `,
          )}

          <!-- Sidebar content (agenda or notes) -->
          ${showSidebar
            ? html`
                <div slot="sidebar" style="display: flex; flex-direction: column; height: 100%; overflow: hidden;">
                  ${this.sidebarContent === 'agenda'
                    ? html`
                        <!-- Fixed header -->
                        <div
                          style="flex: none; display: flex; justify-content: space-between; align-items: center; padding: 20px; background: rgba(255, 255, 255, 0.95); border-bottom: 1px solid #e0e0e0;"
                        >
                          <h3 style="margin: 0; color: #333; font-size: 20px; font-weight: 600;">
                            ${t('viewers.meet.agenda')}
                          </h3>
                          <button
                            @click=${() => (this.sidebarContent = 'none')}
                            style="background: transparent; border: none; font-size: 24px; cursor: pointer; color: #333; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;"
                            title=${t('viewers.meet.close')}
                          >
                            ×
                          </button>
                        </div>
                        <!-- Scrollable content -->
                        <div style="flex: 1; overflow-y: auto; padding: 20px; color: #666; line-height: 1.6;">
                          ${unsafeHTML(this.meetingAgenda)}
                        </div>
                      `
                    : this.sidebarContent === 'notes'
                      ? html`
                          <!-- Fixed header -->
                          <div
                            style="flex: none; display: flex; justify-content: space-between; align-items: center; padding: 20px; background: rgba(255, 255, 255, 0.95); border-bottom: 1px solid #e0e0e0;"
                          >
                            <h3 style="margin: 0; color: #333; font-size: 20px; font-weight: 600;">
                              ${t('viewers.meet.privateNotes')}
                            </h3>
                            <button
                              @click=${() => (this.sidebarContent = 'none')}
                              style="background: transparent; border: none; font-size: 24px; cursor: pointer; color: #333; padding: 0; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;"
                              title=${t('viewers.meet.close')}
                            >
                              ×
                            </button>
                          </div>
                          <!-- Scrollable content area -->
                          <div
                            style="flex: 1; display: flex; flex-direction: column; padding: 20px; overflow: hidden; box-sizing: border-box;"
                          >
                            <textarea
                              style="flex: 1; width: 100%; min-height: 300px; padding: 12px; border: 1px solid #ddd; border-radius: 8px; font-size: 14px; font-family: inherit; resize: none; background: white; color: #333; box-sizing: border-box;"
                              .value=${this.privateNotesContent}
                              @input=${(e: InputEvent) => {
                                const newContent = (e.target as HTMLTextAreaElement).value;
                                this.updatePrivateNotesContent(newContent);
                              }}
                              placeholder=${t('viewers.meet.privateNotesPlaceholder')}
                            ></textarea>
                            <p style="color: #999; font-size: 12px; margin: 12px 0 0 0; flex: none;">
                              ${t('viewers.meet.privateNotesInfo')}
                            </p>
                          </div>
                        `
                      : nothing}
                </div>
              `
            : nothing}
        </d-video-conference-layout>
      </div>
    `;
  }

  private async attachSelfVideo(localPeer: HMSPeer) {
    if (localPeer.videoTrack && this.selfVideoRef.value && this.videoEnabled) {
      try {
        await this.hmsActions.attachVideo(localPeer.videoTrack, this.selfVideoRef.value);
        console.log('Successfully attached self video after render');
      } catch (e) {
        console.error('Failed to attach self video after render:', e);
      }
    }
  }

  private async setupSelfVideoSubscription(localPeer: HMSPeer) {
    if (localPeer.videoTrack) {
      // Try to attach immediately if ref is available
      if (this.selfVideoRef.value) {
        try {
          await this.hmsActions.attachVideo(localPeer.videoTrack, this.selfVideoRef.value);
          console.log('Attached self video immediately');
        } catch (e) {
          console.error('Failed to attach self video:', e);
        }
      }

      // Also subscribe for future changes
      this.hmsStore.subscribe(async (track) => {
        if (!track) return;

        // Wait a bit for the element to be rendered
        await this.updateComplete;

        if (this.selfVideoRef.value) {
          try {
            if (track.enabled) {
              await this.hmsActions.attachVideo(track.id, this.selfVideoRef.value);
              console.log('Attached self video via subscription');
            } else {
              await this.hmsActions.detachVideo(track.id, this.selfVideoRef.value);
              console.log('Detached self video');
            }
          } catch (e) {
            console.error('Error handling self video:', e);
          }
        }
      }, selectVideoTrackByID(localPeer.videoTrack));
    }
  }

  private handleScreenClick(event: MouseEvent) {
    // Don't toggle if clicking on controls themselves or private notes
    const target = event.target as HTMLElement;
    if (
      target.closest('.floating-controls') ||
      target.closest('.top-bar') ||
      target.closest('.device-menu') ||
      target.closest('.private-notes-panel')
    ) {
      return;
    }

    // Toggle controls visibility
    this.showControls = !this.showControls;

    // If showing controls, start auto-hide timer
    if (this.showControls) {
      this.startHideControlsTimer();
    } else {
      // Clear timer if hiding manually
      if (this.hideControlsTimer) {
        clearTimeout(this.hideControlsTimer);
      }
    }

    // Hide device menus when toggling controls
    this.showAudioDevices = false;
    this.showVideoDevices = false;
    this.showAudioOutputDevices = false;
  }

  private startHideControlsTimer() {
    if (this.hideControlsTimer) {
      clearTimeout(this.hideControlsTimer);
    }
    this.hideControlsTimer = window.setTimeout(() => {
      this.showControls = false;
    }, 3000);
  }

  private async onUpdateVideoDevice(deviceId: string) {
    await this.hmsActions.setVideoSettings({ deviceId: deviceId });
  }

  private async onUpdateAudioDevice(deviceId: string) {
    await this.hmsActions.setAudioSettings({ deviceId: deviceId });
  }

  private async onUpdateAudioOutputDevice(deviceId: string) {
    await this.hmsActions.setAudioOutputDevice(deviceId);
  }

  private viewerPropertiesConfig(): ViewerPropertiesConfig | undefined {
    if (!this.organizationId || !this.meetingOccurrenceId || !this.currentEmployeeUuid) {
      return undefined;
    }

    return {
      organizationId: this.organizationId,
      entityType: 'meetingOccurrences',
      entityId: this.meetingOccurrenceId,
      userId: this.currentEmployeeUuid,
    };
  }

  private async initializeViewerProperties(config: ViewerPropertiesConfig): Promise<void> {
    try {
      console.log('[MeetViewer] Initializing YJS provider for:', config);
      const { notesText, cleanup } = await viewerPropertiesManager.getProvider(
        String(config.organizationId),
        'meetingOccurrences',
        config.entityId,
        config.userId,
      );

      // Set up the YJS text
      this.privateNotesText = notesText;

      // Store cleanup function
      this.viewerPropertiesCleanup = cleanup;

      // Initial content load
      this.privateNotesContent = this.privateNotesText.toString();

      // Listen for changes from other users
      this.privateNotesText.observe(() => {
        this.privateNotesContent = this.privateNotesText!.toString();
      });

      this.privateNotesInitialized = true;
      this.privateNotesLoading = false;

      console.log('[MeetViewer] YJS provider initialized successfully');
    } catch (error) {
      console.error('[MeetViewer] Failed to initialize YJS provider:', error);
      this.privateNotesLoading = false;
      // Fall back to empty content
      this.privateNotesContent = '';
    }
  }

  private cleanupViewerProperties() {
    if (this.viewerPropertiesCleanup) {
      this.viewerPropertiesCleanup();
      this.viewerPropertiesCleanup = undefined;
    }
    this.privateNotesText = undefined;
    this.currentViewerPropertiesKey = undefined;
    this.privateNotesInitialized = false;
  }

  private initializePrivateNotes() {
    const config = this.viewerPropertiesConfig();
    if (!config) {
      console.warn('[MeetViewer] Cannot initialize private notes - missing config parameters');
      return;
    }

    // Check if we need to initialize or reinitialize based on config changes
    const configKey = `${config.organizationId}-${config.entityType}-${config.entityId}`;

    if (this.currentViewerPropertiesKey !== configKey) {
      // Clean up old provider
      if (this.viewerPropertiesCleanup) {
        this.cleanupViewerProperties();
      }

      // Set the new key and start initialization
      this.currentViewerPropertiesKey = configKey;
      this.privateNotesLoading = true;
      this.privateNotesInitialized = false;
      this.initializationPromise = this.initializeViewerProperties(config);
    }
  }

  private updatePrivateNotesContent(newContent: string) {
    if (this.privateNotesText && this.privateNotesInitialized) {
      // Update YJS text which will sync to other users
      this.privateNotesText.delete(0, this.privateNotesText.length);
      this.privateNotesText.insert(0, newContent);
    }
    // Update local state immediately for responsive UI
    this.privateNotesContent = newContent;
  }

  private createRef(peer: HMSPeer, v?: Element): Ref<Element> | undefined {
    if (!this.videoRefs.has(peer.id)) {
      this.videoRefs.set(peer.id, createRef());

      this.hmsStore.subscribe(async (track) => {
        console.log('track');
        console.log(peer);
        console.log(track);
        if (!track) {
          return;
        }
        const videoElement = v as HTMLVideoElement;
        console.log(videoElement);

        if (videoElement) {
          if (track.enabled) {
            await this.hmsActions.attachVideo(track.id, videoElement);
          } else {
            await this.hmsActions.detachVideo(track.id, videoElement);
          }
        }
      }, selectVideoTrackByID(peer.videoTrack));
    }

    return this.videoRefs.get(peer.id);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-meet-viewer': DMeetViewer;
  }
}
